/**
 * Utility functions for image annotations (bounding boxes and polygons)
 */

import { BoundingBox, Polygon, Point, ImageAnnotation } from '../types/training';
import { v4 as uuidv4 } from 'uuid';

/**
 * Add a bounding box annotation
 */
export const addBoundingBox = (
  annotations: ImageAnnotation[] | undefined,
  x: number,
  y: number,
  width: number,
  height: number,
  color: string
): ImageAnnotation[] => {
  const newAnnotations = annotations || [];
  const bbox: BoundingBox = {
    id: uuidv4(),
    x,
    y,
    width,
    height,
    color,
    createdAt: Date.now(),
  };
  return [...newAnnotations, bbox];
};

/**
 * Add a polygon annotation
 */
export const addPolygon = (
  annotations: ImageAnnotation[] | undefined,
  points: Point[],
  color: string
): ImageAnnotation[] => {
  const newAnnotations = annotations || [];
  const polygon: Polygon = {
    id: uuidv4(),
    points,
    color,
    createdAt: Date.now(),
  };
  return [...newAnnotations, polygon];
};

/**
 * Remove an annotation by ID
 */
export const removeAnnotation = (
  annotations: ImageAnnotation[] | undefined,
  annotationId: string
): ImageAnnotation[] => {
  if (!annotations) return [];
  return annotations.filter((ann) => ann.id !== annotationId);
};

/**
 * Clear all annotations
 */
export const clearAllAnnotations = (): ImageAnnotation[] => {
  return [];
};

/**
 * Serialize annotations to JSON string
 */
export const serializeAnnotations = (
  annotations: ImageAnnotation[] | undefined
): string => {
  return JSON.stringify(annotations || []);
};

/**
 * Deserialize annotations from JSON string
 */
export const deserializeAnnotations = (
  annotationsJson: string
): ImageAnnotation[] => {
  try {
    return JSON.parse(annotationsJson);
  } catch {
    return [];
  }
};

/**
 * Check if annotation is a bounding box
 */
export const isBoundingBox = (ann: ImageAnnotation): ann is BoundingBox => {
  return 'x' in ann && 'y' in ann && 'width' in ann && 'height' in ann;
};

/**
 * Check if annotation is a polygon
 */
export const isPolygon = (ann: ImageAnnotation): ann is Polygon => {
  return 'points' in ann;
};

/**
 * Draw annotations on canvas
 */
export const drawAnnotationsOnCanvas = (
  ctx: CanvasRenderingContext2D,
  annotations: ImageAnnotation[] | undefined,
  scale: number = 1
): void => {
  if (!annotations || annotations.length === 0) return;

  for (const ann of annotations) {
    ctx.strokeStyle = ann.color;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.8;

    if (isBoundingBox(ann)) {
      ctx.strokeRect(
        ann.x * scale,
        ann.y * scale,
        ann.width * scale,
        ann.height * scale
      );
    } else if (isPolygon(ann)) {
      if (ann.points.length > 0) {
        ctx.beginPath();
        ctx.moveTo(ann.points[0].x * scale, ann.points[0].y * scale);
        for (let i = 1; i < ann.points.length; i++) {
          ctx.lineTo(ann.points[i].x * scale, ann.points[i].y * scale);
        }
        ctx.closePath();
        ctx.stroke();
      }
    }
  }

  ctx.globalAlpha = 1;
};

/**
 * Get annotation at mouse position
 */
export const getAnnotationAtPosition = (
  annotations: ImageAnnotation[] | undefined,
  x: number,
  y: number,
  scale: number = 1
): ImageAnnotation | undefined => {
  if (!annotations) return undefined;

  for (const ann of annotations) {
    if (isBoundingBox(ann)) {
      const scaledX = ann.x * scale;
      const scaledY = ann.y * scale;
      const scaledWidth = ann.width * scale;
      const scaledHeight = ann.height * scale;

      if (
        x >= scaledX &&
        x <= scaledX + scaledWidth &&
        y >= scaledY &&
        y <= scaledY + scaledHeight
      ) {
        return ann;
      }
    } else if (isPolygon(ann)) {
      if (isPointInPolygon(x, y, ann.points, scale)) {
        return ann;
      }
    }
  }

  return undefined;
};

/**
 * Check if point is inside polygon using ray casting algorithm
 */
const isPointInPolygon = (
  x: number,
  y: number,
  points: Point[],
  scale: number
): boolean => {
  if (points.length < 3) return false;

  let isInside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x * scale;
    const yi = points[i].y * scale;
    const xj = points[j].x * scale;
    const yj = points[j].y * scale;

    const intersect =
      (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) isInside = !isInside;
  }

  return isInside;
};

/**
 * Rescale all annotations when image dimensions change
 */
export const rescaleAnnotations = (
  annotations: ImageAnnotation[] | undefined,
  oldScale: number,
  newScale: number
): ImageAnnotation[] => {
  if (!annotations) return [];

  const scaleFactor = newScale / oldScale;

  return annotations.map((ann) => {
    if (isBoundingBox(ann)) {
      return {
        ...ann,
        x: ann.x * scaleFactor,
        y: ann.y * scaleFactor,
        width: ann.width * scaleFactor,
        height: ann.height * scaleFactor,
      };
    } else if (isPolygon(ann)) {
      return {
        ...ann,
        points: ann.points.map((p) => ({
          x: p.x * scaleFactor,
          y: p.y * scaleFactor,
        })),
      };
    }
    return ann;
  });
};
